import type { ContractReviewForm } from "../types/template";
import type { HighlightMergeFieldKey } from "../types/templateMerge";
import {
  HIGHLIGHT_LABEL_RULES,
  mergeFieldForHighlightText,
  normalizeHighlightLabelText
} from "../constants/highlightLabelMap";
import { getFormValueForMergeField } from "./mergeFieldValues";
import { formatDateForDocumentMergeLong } from "../utils/displayDate";
import { findLogicalYellowHighlightRanges } from "./highlightScanner";

type ContractReviewMergeFieldKey =
  | HighlightMergeFieldKey
  | "businessUnit"
  | "contractOrigination";

type ContractReviewTaggedUpdate = {
  field: ContractReviewMergeFieldKey;
  newValue: string;
};

const FIELD_LABEL: Record<HighlightMergeFieldKey, string> = {
  effectiveDate: "Effective date",
  contactName: "Client / contact name",
  legalEntityName: "Company Name",
  representativeName: "Representative name",
  representativeTitle: "Representative title",
  representativeCompanyName: "Representative company name",
  representativeAddress: "Representative address",
  representativeStreetAddress: "Representative street address",
  representativeCityStateZip: "Representative city, state, ZIP",
  representativePhone: "Representative phone",
  representativeEmail: "Representative email",
  clientAddress: "Client address",
  companyStreetAddress: "Company street address",
  companyCityStateZip: "Company city, state, ZIP",
  msaEffectiveDate: "MSA effective date",
  planGroupName: "Plan / group name",
  contractName: "Contract Name",
  contactTitle: "Contact Title",
  phone: "Phone",
  email: "Email"
};

export type HighlightApplyReplacement = {
  mergeField: HighlightMergeFieldKey;
  fieldLabel: string;
  documentTextBefore: string;
  newValue: string;
};

export type HighlightSkippedHighlight = {
  index: number;
  text: string;
  reason: string;
};

/**
 * Yellow highlights: label map in `constants/highlightLabelMap.ts`.
 * Second pass: plain `[BRACKET]` tokens (same labels as the map, plus date-prefixed effective-date
 * variants and a few document tokens that are not yellow-scanned). `{{Mustache}}` is not searched.
 */
export type ApplyPlaceholdersResult = {
  yellowHighlightCount: number;
  yellowHighlightTexts: string[];
  highlightReplaced: HighlightApplyReplacement[];
  highlightSkipped: HighlightSkippedHighlight[];
  highlightMessage?: string;
  taggedUpdated: ContractReviewTaggedUpdate[];
  /** Non–yellow-highlight replacements from the bracket/token pass (name kept for callers). */
  mustacheReplaced: Array<{
    placeholder: string;
    field: ContractReviewMergeFieldKey;
    newValue: string;
  }>;
};

export const TRACK_CHANGES_APPLY_FAILED_PREFIX = "TRACK_CHANGES_APPLY_FAILED";
const CONTRACT_REVIEW_TAG_PREFIX = "CONTRACT_REVIEW_";

const CONTRACT_REVIEW_FIELDS: readonly ContractReviewMergeFieldKey[] = [
  "effectiveDate",
  "contactName",
  "legalEntityName",
  "representativeName",
  "representativeTitle",
  "representativeCompanyName",
  "representativeAddress",
  "representativeStreetAddress",
  "representativeCityStateZip",
  "representativePhone",
  "representativeEmail",
  "clientAddress",
  "companyStreetAddress",
  "companyCityStateZip",
  "msaEffectiveDate",
  "planGroupName",
  "contractName",
  "contactTitle",
  "phone",
  "email",
  "businessUnit",
  "contractOrigination"
];

const tagForContractReviewField = (field: ContractReviewMergeFieldKey): string =>
  `${CONTRACT_REVIEW_TAG_PREFIX}${field.replace(/[A-Z]/g, (c) => `_${c}`).toUpperCase()}`;

const getFormValueForContractReviewField = (
  field: ContractReviewMergeFieldKey,
  form: ContractReviewForm
): string => {
  switch (field) {
    case "businessUnit":
      return form.businessUnit.trim();
    case "contractOrigination":
      return form.contractOrigination.trim();
    default:
      return getFormValueForMergeField(field, form);
  }
};

const emptyApplyPlaceholdersResult = (): ApplyPlaceholdersResult => ({
  yellowHighlightCount: 0,
  yellowHighlightTexts: [],
  highlightReplaced: [],
  highlightSkipped: [],
  taggedUpdated: [],
  mustacheReplaced: []
});

/** Read current mode; set **TrackAll** when needed. Caller restores `savedMode` in `finally`. */
const readChangeTrackingMode = async (
  context: Word.RequestContext
): Promise<Word.ChangeTrackingMode | "Off" | "TrackAll" | "TrackMineOnly"> => {
  const doc = context.document;
  doc.load("changeTrackingMode");
  await context.sync();
  return doc.changeTrackingMode;
};

const enableTrackAllIfNeeded = async (
  context: Word.RequestContext,
  savedMode: Word.ChangeTrackingMode | "Off" | "TrackAll" | "TrackMineOnly"
): Promise<void> => {
  const alreadyTracking = String(savedMode) === String(Word.ChangeTrackingMode.trackAll);
  if (!alreadyTracking) {
    context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
    await context.sync();
  }
};

/** Replaces range content with value; with Track Changes on, Word records revision markup. Optionally tags with a content control. */
const replaceRangeWithTrackedValue = (
  range: Word.Range,
  newValue: string,
  field?: ContractReviewMergeFieldKey
): Word.Range => {
  const inserted = range.insertText(newValue, Word.InsertLocation.replace);
  if (field) {
    const cc = inserted.insertContentControl(Word.ContentControlType.richText);
    cc.tag = tagForContractReviewField(field);
    cc.title = FIELD_LABEL[field as HighlightMergeFieldKey] ?? field;
    cc.cannotDelete = false;
  }
  return inserted;
};

const clearInsertedHighlightWithoutRevision = async (
  context: Word.RequestContext,
  ranges: readonly Word.Range[]
): Promise<void> => {
  if (ranges.length === 0) {
    return;
  }

  const currentMode = await readChangeTrackingMode(context);
  try {
    context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
    await context.sync();
    for (const range of ranges) {
      range.font.set({
        highlightColor: ""
      });
    }
    await context.sync();
  } finally {
    try {
      context.document.changeTrackingMode = currentMode;
      await context.sync();
    } catch {
      /* best-effort restore */
    }
  }
};

/** Pairwise compares per sync stay below typical Office.js batch limits. */
const MAX_GEOMETRIC_REPLACEMENT_OPS = 48;
const MAX_COMPARE_PAIRS_PER_SYNC = 900;

type BracketTokenReplacementOp = {
  range: Word.Range;
  placeholder: string;
  field: ContractReviewMergeFieldKey;
  newValue: string;
  /** Original merge order (longer tokens / earlier search jobs first) for tie-breaks. */
  seq: number;
};

const relationKey = (i: number, j: number): string => `${Math.min(i, j)},${Math.max(i, j)}`;

/** After sync(), Office populates `.value` on the object returned by `compareLocationWith`. */
const readLocationRelation = (queued: unknown): Word.LocationRelation => {
  const v = (queued as { value?: Word.LocationRelation }).value;
  return v ?? ("Equal" as Word.LocationRelation);
};

/**
 * True if ops[aIdx] is strictly after ops[bIdx] in document reading order.
 * Uses only `compareLocationWith` for i < j; inverts when aIdx > bIdx.
 */
const isRangeAfterInDocument = (
  relByPair: ReadonlyMap<string, Word.LocationRelation>,
  aIdx: number,
  bIdx: number
): boolean | null => {
  if (aIdx === bIdx) {
    return false;
  }
  if (aIdx < bIdx) {
    const r = relByPair.get(relationKey(aIdx, bIdx));
    if (r === undefined) {
      return null;
    }
    if (r === Word.LocationRelation.after || r === Word.LocationRelation.adjacentAfter) {
      return true;
    }
    if (r === Word.LocationRelation.before || r === Word.LocationRelation.adjacentBefore) {
      return false;
    }
    return null;
  }
  const r = relByPair.get(relationKey(bIdx, aIdx));
  if (r === undefined) {
    return null;
  }
  if (r === Word.LocationRelation.before || r === Word.LocationRelation.adjacentBefore) {
    return true;
  }
  if (r === Word.LocationRelation.after || r === Word.LocationRelation.adjacentAfter) {
    return false;
  }
  return null;
};

const buildPairwiseLocationRelations = async (
  context: Word.RequestContext,
  ops: readonly BracketTokenReplacementOp[]
): Promise<Map<string, Word.LocationRelation>> => {
  const n = ops.length;
  const relByPair = new Map<string, Word.LocationRelation>();
  const pairs: Array<[number, number]> = [];
  for (let i = 0; i < n; i += 1) {
    for (let j = i + 1; j < n; j += 1) {
      pairs.push([i, j]);
    }
  }
  let offset = 0;
  while (offset < pairs.length) {
    const slice = pairs.slice(offset, offset + MAX_COMPARE_PAIRS_PER_SYNC);
    offset += slice.length;
    const queued: Array<{ i: number; j: number; cmp: unknown }> = [];
    for (const [i, j] of slice) {
      queued.push({ i, j, cmp: ops[i].range.compareLocationWith(ops[j].range) });
    }
    await context.sync();
    for (const { i, j, cmp } of queued) {
      relByPair.set(relationKey(i, j), readLocationRelation(cmp));
    }
  }
  return relByPair;
};

const sortReplacementOpsForEndToStart = async (
  context: Word.RequestContext,
  ops: BracketTokenReplacementOp[]
): Promise<BracketTokenReplacementOp[]> => {
  const n = ops.length;
  if (n <= 1) {
    return ops;
  }
  const relByPair = await buildPairwiseLocationRelations(context, ops);
  const indices = ops.map((_, i) => i);
  indices.sort((a, b) => {
    const aAfterB = isRangeAfterInDocument(relByPair, a, b);
    const bAfterA = isRangeAfterInDocument(relByPair, b, a);
    if (aAfterB === true && bAfterA !== true) {
      return -1;
    }
    if (bAfterA === true && aAfterB !== true) {
      return 1;
    }
    const pa = ops[a].placeholder.length;
    const pb = ops[b].placeholder.length;
    if (pa !== pb) {
      return pb - pa;
    }
    const pc = ops[a].placeholder.localeCompare(ops[b].placeholder);
    if (pc !== 0) {
      return pc;
    }
    return ops[a].seq - ops[b].seq;
  });
  return indices.map((i) => ops[i]);
};

/** One `sync` per search — slow on templates with many token variants. */
const applyBracketTokenPlaceholdersSequential = async (
  context: Word.RequestContext,
  body: Word.Body,
  placeholderEntries: ReadonlyArray<
    readonly [string, { readonly field: ContractReviewMergeFieldKey; readonly newValue: string }]
  >
): Promise<ApplyPlaceholdersResult["mustacheReplaced"]> => {
  const replaced: ApplyPlaceholdersResult["mustacheReplaced"] = [];
  const insertedRanges: Word.Range[] = [];
  for (const [placeholder, { field, newValue }] of placeholderEntries) {
    if (!newValue) {
      continue;
    }
    const results = body.search(placeholder, {
      matchCase: false,
      matchWholeWord: false
    });
    context.load(results, "items");
    await context.sync();
    for (const range of results.items) {
      insertedRanges.push(replaceRangeWithTrackedValue(range, newValue, field));
      replaced.push({ placeholder, field, newValue });
    }
  }
  await context.sync();
  await clearInsertedHighlightWithoutRevision(context, insertedRanges);
  return replaced;
};

const applyBracketTokenPlaceholders = async (
  context: Word.RequestContext,
  form: ContractReviewForm,
  highlightReplaced: HighlightApplyReplacement[]
): Promise<ApplyPlaceholdersResult["mustacheReplaced"]> => {
  const body = context.document.body;
  const replaced: ApplyPlaceholdersResult["mustacheReplaced"] = [];
  const skipBracketForLabel = new Set(
    highlightReplaced.map((r) => normalizeHighlightLabelText(r.documentTextBefore))
  );

  const placeholderMap: Record<string, { field: ContractReviewMergeFieldKey; newValue: string }> = {};

  for (const rule of HIGHLIGHT_LABEL_RULES) {
    const newValue = getFormValueForMergeField(rule.mergeField, form);
    if (!newValue) {
      continue;
    }
    for (const label of rule.labels) {
      if (skipBracketForLabel.has(label)) {
        continue;
      }
      placeholderMap[`[${label}]`] = { field: rule.mergeField, newValue };
    }
  }

  /** Bracket spellings for form fields not listed in {@link HIGHLIGHT_LABEL_RULES} (no `{{…}}` pass). */
  const extraBracketTokens: Array<[string, ContractReviewMergeFieldKey, string]> = [
    ["[CONTRACT NAME]", "contractName", form.contractName.trim()],
    ["[BUSINESS UNIT]", "businessUnit", form.businessUnit.trim()],
    ["[CONTRACT ORIGINATION]", "contractOrigination", form.contractOrigination.trim()]
  ];
  for (const [key, field, val] of extraBracketTokens) {
    if (!val || key in placeholderMap) {
      continue;
    }
    placeholderMap[key] = { field, newValue: val };
  }

  const iso = form.effectiveDate.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    const formattedEffective = formatDateForDocumentMergeLong(form.effectiveDate);
    if (formattedEffective) {
      placeholderMap[`${iso}[EFFECTIVE DATE]`] = {
        field: "effectiveDate",
        newValue: formattedEffective
      };
      placeholderMap[`${iso}[MSA EFFECTIVE DATE]`] = {
        field: "msaEffectiveDate",
        newValue: formattedEffective
      };
    }
  }

  const placeholderEntries = Object.entries(placeholderMap).sort(
    (a, b) => b[0].length - a[0].length
  ) as Array<[string, { field: ContractReviewMergeFieldKey; newValue: string }]>;

  const searchJobs: Array<{
    placeholder: string;
    field: ContractReviewMergeFieldKey;
    newValue: string;
    results: Word.RangeCollection;
  }> = [];

  for (const [placeholder, { field, newValue }] of placeholderEntries) {
    if (!newValue) {
      continue;
    }
    const results = body.search(placeholder, {
      matchCase: false,
      matchWholeWord: false
    });
    context.load(results, "items");
    searchJobs.push({ placeholder, field, newValue, results });
  }
  await context.sync();

  let seq = 0;
  const ops: BracketTokenReplacementOp[] = [];
  for (const job of searchJobs) {
    for (const range of job.results.items) {
      ops.push({
        range,
        placeholder: job.placeholder,
        field: job.field,
        newValue: job.newValue,
        seq
      });
      seq += 1;
    }
  }

  if (ops.length === 0) {
    return replaced;
  }

  if (ops.length > MAX_GEOMETRIC_REPLACEMENT_OPS) {
    return applyBracketTokenPlaceholdersSequential(context, body, placeholderEntries);
  }

  const ordered = await sortReplacementOpsForEndToStart(context, ops);
  const insertedRanges: Word.Range[] = [];
  for (const op of ordered) {
    insertedRanges.push(
      replaceRangeWithTrackedValue(op.range, op.newValue, op.field)
    );
    replaced.push({ placeholder: op.placeholder, field: op.field, newValue: op.newValue });
  }
  await context.sync();
  await clearInsertedHighlightWithoutRevision(context, insertedRanges);
  return replaced;
};

const applyYellowHighlightsByLabel = async (
  context: Word.RequestContext,
  form: ContractReviewForm
): Promise<
  Pick<
    ApplyPlaceholdersResult,
    | "yellowHighlightCount"
    | "yellowHighlightTexts"
    | "highlightReplaced"
    | "highlightSkipped"
    | "highlightMessage"
  >
> => {
  const body = context.document.body;
  const logicalRanges = await findLogicalYellowHighlightRanges(context, body);

  const yellowHighlightTexts = logicalRanges.map((r) => (r.text ?? "").trim());

  const highlightReplaced: HighlightApplyReplacement[] = [];
  const highlightSkipped: HighlightSkippedHighlight[] = [];
  const insertedRanges: Word.Range[] = [];
  const messageParts: string[] = [];

  if (logicalRanges.length === 0) {
    messageParts.push("No yellow highlighted text was found in the document body.");
  }

  for (let i = 0; i < logicalRanges.length; i++) {
    const range = logicalRanges[i];
    const documentTextBefore = yellowHighlightTexts[i] ?? "";
    const field = mergeFieldForHighlightText(documentTextBefore);

    if (!field) {
      highlightSkipped.push({
        index: i,
        text: documentTextBefore,
        reason:
          "No merge field for this label — add or fix a label in addin/src/constants/highlightLabelMap.ts"
      });
      continue;
    }

    const newValue = getFormValueForMergeField(field, form);
    if (!newValue) {
      highlightSkipped.push({
        index: i,
        text: documentTextBefore,
        reason: `No form value for ${FIELD_LABEL[field]} (${field})`
      });
      continue;
    }

    insertedRanges.push(replaceRangeWithTrackedValue(range, newValue, field));
    highlightReplaced.push({
      mergeField: field,
      fieldLabel: FIELD_LABEL[field],
      documentTextBefore,
      newValue
    });
  }

  await context.sync();
  await clearInsertedHighlightWithoutRevision(context, insertedRanges);

  return {
    yellowHighlightCount: yellowHighlightTexts.length,
    yellowHighlightTexts,
    highlightReplaced,
    highlightSkipped,
    highlightMessage: messageParts.length ? messageParts.join(" ") : undefined
  };
};

const updateTaggedContractReviewControls = async (
  context: Word.RequestContext,
  form: ContractReviewForm
): Promise<ContractReviewTaggedUpdate[]> => {
  const updated: ContractReviewTaggedUpdate[] = [];
  for (const field of CONTRACT_REVIEW_FIELDS) {
    const tag = tagForContractReviewField(field);
    const newValue = getFormValueForContractReviewField(field, form);
    if (!newValue) {
      continue;
    }

    let index = 0;
    let guard = 0;
    while (guard < 200) {
      guard += 1;
      const ccs = context.document.contentControls.getByTag(tag);
      ccs.load("items");
      await context.sync();
      if (ccs.items.length === 0 || index >= ccs.items.length) {
        break;
      }
      const cc = ccs.items[index];
      cc.load("type");
      await context.sync();
      const typeStr = String(cc.type);
      if (
        typeStr.includes("CheckBox") ||
        typeStr.includes("DropDownList") ||
        typeStr.includes("ComboBox") ||
        typeStr.includes("BuildingBlockGallery") ||
        typeStr.includes("DatePicker") ||
        typeStr.includes("Picture")
      ) {
        index += 1;
        continue;
      }
      cc.insertText(newValue, Word.InsertLocation.replace);
      updated.push({ field, newValue });
      await context.sync();
      index += 1;
    }
  }
  return updated;
};

/**
 * Writes Contract Review values into the open Word document.
 * Yellow: label map. Then plain `[LABEL]` tokens (same map; brackets skipped if yellow already replaced that label).
 */
export const applyPlaceholders = async (
  form: ContractReviewForm
): Promise<ApplyPlaceholdersResult | undefined> => {
  if (typeof Word === "undefined") {
    return undefined;
  }

  return Word.run(async (context) => {
    const savedMode = await readChangeTrackingMode(context);
    try {
      context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
      await context.sync();
      const taggedUpdated = await updateTaggedContractReviewControls(context, form);
      if (taggedUpdated.length > 0) {
        return {
          ...emptyApplyPlaceholdersResult(),
          taggedUpdated
        };
      }

      await enableTrackAllIfNeeded(context, savedMode);
      const highlightPart = await applyYellowHighlightsByLabel(context, form);
      const mustacheReplaced = await applyBracketTokenPlaceholders(
        context,
        form,
        highlightPart.highlightReplaced
      );
      return {
        ...highlightPart,
        taggedUpdated: [],
        mustacheReplaced
      };
    } catch (err: unknown) {
      const isOfficeError = typeof err === "object" && err !== null && "code" in err;
      const message = err instanceof Error ? err.message : String(err);
      if (
        isOfficeError ||
        /changeTracking|trackAll|TrackAll|protected|read-only/i.test(message)
      ) {
        throw new Error(
          `${TRACK_CHANGES_APPLY_FAILED_PREFIX}: Word may have Track Changes locked or the document protected. ${message}`,
          { cause: err }
        );
      }
      throw err;
    } finally {
      try {
        context.document.changeTrackingMode = savedMode;
        await context.sync();
      } catch {
        /* best-effort restore */
      }
    }
  });
};

export const finalizeContractReviewPlaceholders = async (): Promise<void> => {
  if (typeof Word === "undefined") {
    return;
  }

  await Word.run(async (context) => {
    for (const field of CONTRACT_REVIEW_FIELDS) {
      const tag = tagForContractReviewField(field);
      let guard = 0;
      while (guard < 200) {
        guard += 1;
        const ccs = context.document.contentControls.getByTag(tag);
        ccs.load("items");
        await context.sync();
        if (ccs.items.length === 0) {
          break;
        }
        ccs.items[ccs.items.length - 1].delete(true);
        await context.sync();
      }
    }
  });
};
